import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCheck,
  IconDots,
  IconPlus,
  IconSend,
  IconTrash,
} from "@tabler/icons-react";
import type { AppointmentForm } from "../../../../types/appointmentForms";
import type { GetTransactionsResponse } from "../../../../types/transactions";
import type { Client } from "../../../../types/client";
import { useAppointmentForms } from "../../../../hooks/useAppointmentForms";
import { useSendFormReminder } from "../../../../hooks/useSendFormReminder";
import { clientFormStatusTag as formStatusDisplay } from "../../../../utils/clientFormStatus";
import VerbalConfirmationModal from "../components/TransactionDetailModal/VerbalConfirmationModal";
import ScheduledRemindersPanel from "../components/TransactionDetailModal/ScheduledRemindersPanel";
import NestedScreen from "../drawer/NestedScreen";
import { NoteCard } from "./NoteCard";
import { usePreviewClientNotes } from "./usePreviewClientNotes";
import styles from "./AppointmentDrawer.module.css";

/**
 * The appointment's nested screens (Forms, Notes, Scheduled messages):
 * shared by the desktop drawer (inside CalendarDrawer) and the mobile detail
 * sheet (inside its full-screen layer).
 */
/** A nested screen's "…" menu: right of its title, holding its actions. */
function ScreenMenu({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Menu position="bottom-end" width={250} radius={12} shadow="md">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={32}
          radius="xl"
          aria-label={label}
        >
          <IconDots size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>{children}</Menu.Dropdown>
    </Menu>
  );
}

// ── Nested: Forms ─────────────────────────────────────────────────────────

export function FormsScreen({
  transaction,
  formsApi,
  status,
  onBack,
  onOpenForm,
  onAddForm,
  formsAlreadySent,
  onFormsSent,
}: {
  transaction: GetTransactionsResponse;
  formsApi: ReturnType<typeof useAppointmentForms>;
  status: string | null;
  onBack: () => void;
  onOpenForm?: (txn: GetTransactionsResponse, formId: string) => void;
  onAddForm?: (txn: GetTransactionsResponse) => void;
  formsAlreadySent?: boolean;
  onFormsSent?: (transactionUuid: string) => void;
}) {
  const txnId = transaction.transaction.id;
  const { forms, loading, pendingCount, remove, deleting } = formsApi;
  const [confirmRemove, setConfirmRemove] = useState<AppointmentForm | null>(
    null,
  );
  const send = useSendFormReminder(txnId, {
    onSent: () => onFormsSent?.(txnId),
  });
  const sent = send.sent || formsAlreadySent;
  const canSend = pendingCount > 0 && status !== "Cancelled";

  return (
    <NestedScreen
      title="Forms"
      onBack={onBack}
      loading={loading && forms.length === 0}
      actions={
        <ScreenMenu label="Form actions">
          <Menu.Item
            leftSection={<IconPlus size={15} />}
            onClick={() => onAddForm?.(transaction)}
          >
            Add form
          </Menu.Item>
          <Menu.Item
            leftSection={
              sent ? <IconCheck size={15} /> : <IconSend size={15} />
            }
            disabled={!canSend || sent || send.sending}
            onClick={send.sendReminder}
          >
            {sent ? "Forms sent" : "Send incomplete forms"}
          </Menu.Item>
        </ScreenMenu>
      }
      footer={
        <Button
          color="teal"
          fullWidth
          leftSection={<IconPlus size={16} />}
          onClick={() => onAddForm?.(transaction)}
        >
          Add form
        </Button>
      }
    >
      {forms.length === 0 ? (
        <Text size="sm" c="dimmed">
          No forms yet. Add an intake, waiver, or SOAP note.
        </Text>
      ) : (
        <Stack gap={6}>
          {forms.map((form) => {
            const display = formStatusDisplay(form.status);
            const confirming = confirmRemove?.uuid === form.uuid;
            return (
              <Group
                key={form.uuid}
                wrap="nowrap"
                gap={6}
                className={styles.formRow}
              >
                <UnstyledButton
                  className={styles.formName}
                  onClick={() => onOpenForm?.(transaction, form.uuid)}
                >
                  <Text size="sm" lineClamp={1}>
                    {form.name}
                  </Text>
                </UnstyledButton>
                {confirming ? (
                  <Group gap={4} wrap="nowrap">
                    <Button
                      size="compact-xs"
                      color="red"
                      loading={deleting}
                      onClick={async () => {
                        await remove(form);
                        setConfirmRemove(null);
                      }}
                    >
                      Remove
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="default"
                      onClick={() => setConfirmRemove(null)}
                    >
                      Keep
                    </Button>
                  </Group>
                ) : (
                  <>
                    <Badge size="sm" variant="light" color={display.color}>
                      {display.label}
                    </Badge>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Remove ${form.name}`}
                      onClick={() => setConfirmRemove(form)}
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </>
                )}
              </Group>
            );
          })}
        </Stack>
      )}
      <VerbalConfirmationModal
        opened={send.verbalConfirmOpen}
        onClose={() => send.setVerbalConfirmOpen(false)}
        onConfirm={send.confirmVerbalSend}
        isLoading={send.sending}
      />
    </NestedScreen>
  );
}

// ── Nested: Notes ─────────────────────────────────────────────────────────

export function NotesScreen({
  notesApi,
  client,
  onBack,
}: {
  notesApi: ReturnType<typeof usePreviewClientNotes>;
  client: Client | null;
  onBack: () => void;
}) {
  const { notes, cachedClient, togglePin, saveNote, removeNote } = notesApi;
  // The add / edit note screen, over the list (a nested drawer of its own).
  const [editor, setEditor] = useState<NoteEditorTarget | null>(null);
  // Pinned first, then newest.
  const ordered = useMemo(
    () =>
      [...notes].sort(
        (a, b) =>
          Number(!!b.is_popup) - Number(!!a.is_popup) ||
          new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime(),
      ),
    [notes],
  );

  return (
    <>
      <NestedScreen
        title="Notes"
        onBack={onBack}
        loading={!cachedClient?.notes && !!client}
        actions={
          client ? (
            <ScreenMenu label="Note actions">
              <Menu.Item
                leftSection={<IconPlus size={15} />}
                onClick={() => setEditor({ text: "" })}
              >
                Add note
              </Menu.Item>
            </ScreenMenu>
          ) : undefined
        }
      >
        <Stack gap={8}>
          {ordered.length === 0 && (
            <Text size="sm" c="dimmed">
              No notes yet.
            </Text>
          )}
          {ordered.map((n) => (
            <NoteCard
              key={n.id}
              text={n.text}
              author={n.created_by_name}
              date={n.created_dt}
              clientProvided={!!n.is_visible_by_client}
              pinned={!!n.is_popup}
              full
              onTogglePin={() => togglePin(n)}
              onEdit={() =>
                setEditor({ id: n.id, text: n.text, is_popup: n.is_popup })
              }
              onDelete={() => void removeNote(n.id)}
            />
          ))}
        </Stack>
      </NestedScreen>
      {editor && (
        <NoteEditorScreen
          target={editor}
          onSave={async (text) => {
            await saveNote(
              editor.id
                ? { id: editor.id, text, is_popup: editor.is_popup }
                : { text },
            );
            setEditor(null);
          }}
          onBack={() => setEditor(null)}
        />
      )}
    </>
  );
}

type NoteEditorTarget = { id?: string; text: string; is_popup?: boolean };

/** Add / edit one note: its own nested screen over the Notes list. */
function NoteEditorScreen({
  target,
  onSave,
  onBack,
}: {
  target: NoteEditorTarget;
  onSave: (text: string) => Promise<void>;
  onBack: () => void;
}) {
  const [text, setText] = useState(target.text);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onSave(text.trim());
    } finally {
      setSaving(false);
    }
  };
  return (
    <NestedScreen
      title={target.id ? "Edit note" : "Add note"}
      onBack={onBack}
      footer={
        <>
          <Box style={{ flex: 1 }} />
          <Button variant="default" onClick={onBack} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="teal"
            onClick={save}
            loading={saving}
            disabled={!text.trim()}
          >
            Save
          </Button>
        </>
      }
    >
      <Textarea
        autosize
        minRows={6}
        data-autofocus
        autoFocus
        placeholder="Write a note"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          // Escape backs out to the Notes list, not the whole drawer.
          if (e.key !== "Escape") return;
          e.preventDefault();
          e.stopPropagation();
          onBack();
        }}
        aria-label={target.id ? "Edit note" : "New note"}
      />
    </NestedScreen>
  );
}

// ── Nested: Scheduled messages ────────────────────────────────────────────

export function MessagesScreen({
  transactionUuid,
  timezone,
  hasPendingForms,
  status,
  onBack,
}: {
  transactionUuid: string;
  timezone: string;
  hasPendingForms: boolean;
  status: string | null;
  onBack: () => void;
}) {
  // The panel's loaded count, in the screen title (the panel has none here).
  const [count, setCount] = useState<number | null>(null);
  return (
    <NestedScreen
      title={count ? `Scheduled messages (${count})` : "Scheduled messages"}
      onBack={onBack}
    >
      {/* Read-only here: Send now / Retry / Cancel stay in Appointment
          Details. */}
      <ScheduledRemindersPanel
        transactionUuid={transactionUuid}
        timezone={timezone}
        canEdit={false}
        showTitle={false}
        onCountChange={setCount}
        hasPendingForms={hasPendingForms}
        appointmentStatus={status}
      />
    </NestedScreen>
  );
}
